import { Avatar } from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { DateEditor } from "../../components/OverlayComponents/DateEditor";
import { useUpdateTask } from "../../data/mutations/taskMutations";
import { useBoardPermissions } from "../../data/queries/boardQueries";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import { DateFormats } from "../../types/DateFormats";
import { formatDate } from "../../utils/helpers";
import { StageDropdown } from "./StageDropdown";
import { TaskCollaboratorsContainer } from "./TaskCollaboratorsContainer";
import { TaskDetail } from "./TaskDetail";
import { UsersDropdownInPlaceEditor } from "./UsersDropdownInPlaceEditor";

export const TaskInformationContainer = ({ ids, task, canModifyTasks }) => {
  const { t } = useTranslation(["Overlay", "Shared"], {
    useSuspense: false,
  });

  const { UserId } = useDueDatesAuth();
  const { mutate: updateTask } = useUpdateTask();
  const queryClient = useQueryClient();
  const { Events, toggleEvent } = useEvent();
  const { data: boardPermissions, isPending: isBoardPermissionsPending } =
    useBoardPermissions(task?.BoardId);

  const dueDateRef = useRef(null);
  const startDateRef = useRef(null);

  const users = boardPermissions?.Entities;
  const assigneeName = users?.find(
    (x) => x.FullName == task?.Assignee?.FullName
  )
    ? task?.Assignee?.FullName
    : null;

  const onTaskModified = (e) => {
    let modifiedTask = {
      ...task,
      TemplateId: task?.Template?.Id,
      CurrentUserId: UserId,
      Collaborators: null,
      Tags: null,
      AssigneeId: task?.Assignee?.Id,
    };

    if (e?.id) {
      // this triggers on `actionSuccess` event of InPlaceEditors
      const value = e.value;
      switch (e.id) {
        case ids.assignee:
          if (value == task?.Assignee?.Id || (!value && !task?.Assignee?.Id))
            return;
          modifiedTask = {
            ...modifiedTask,
            AssigneeId: value,
          };
          break;
        case ids.dueDate:
          if (
            formatDate(
              value,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            ) ==
            formatDate(
              task?.DueDate,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            )
          )
            return;

          modifiedTask = {
            ...modifiedTask,
            DueDate: value,
          };
          break;
        case ids.startDate:
          if (
            formatDate(
              value,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            ) ==
            formatDate(
              task?.DueDate,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            )
          )
            return;

          modifiedTask = {
            ...modifiedTask,
            StartDate: value,
          };
          break;
        case ids.stage:
          if (value?.Id == task?.Stage.Id) return;
          modifiedTask = {
            ...modifiedTask,
            StageId: value?.Id,
          };
          break;
        default:
          break;
      }
    }

    updateTask(
      { task: modifiedTask },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["get-task", task?.Id]);
          toggleEvent(Events.RefreshTaskGrid);
          toast.success(t("Success"), { position: "bottom-left" });
        },
        onError: () => {
          toast.error(t("ErrorMsg"), { position: "bottom-left" });
        },
      }
    );
  };

  return (
    <>
      <div className="task-information-container my-2">
        <StageDropdown
          id={ids.stage}
          initialValue={task?.Stage}
          boardId={task?.BoardId}
          change={onTaskModified}
          disabled={!canModifyTasks}
        />
      </div>
      <div className="task-details br-4 bc-bg-light-gray">
        <TaskDetail containerCss="bb-1-light-gray">
          <div className="my-3 d-flex flex-column gap-2">
            <label className="fw-600 fs-14 et-black" htmlFor={ids.startDate}>
              {t("StartDate")}
            </label>
            <DateEditor
              ref={startDateRef}
              id={ids.startDate}
              label={t("StartDate")}
              task={task}
              value={task?.StartDate}
              model={{ openOnFocus: false, showClearButton: true }}
              inlineActionSuccess={(e) => {
                e.id = ids.startDate;
                e.value = startDateRef.current?.getValue();
                onTaskModified(e);
              }}
              canModifyTasks={canModifyTasks}
            />
            <label className="fw-600 fs-14 et-black" htmlFor={ids.dueDate}>
              {t("DueDate")}
            </label>
            <DateEditor
              ref={dueDateRef}
              id={ids.dueDate}
              task={task}
              value={task?.DueDate}
              inlineActionSuccess={(e) => {
                e.id = ids.dueDate;
                e.value = dueDateRef.current?.getValue();
                onTaskModified(e);
              }}
              canModifyTasks={canModifyTasks}
              isDueDate={true}
            />
          </div>
        </TaskDetail>
        <TaskDetail label={t("Assignee")} containerCss="bb-1-light-gray">
          <div className="d-flex justify-content-start align-items-center gap-2">
            <Avatar content={assigneeName} />
            <UsersDropdownInPlaceEditor
              id={ids.assignee}
              boardPermissions={boardPermissions}
              loading={isBoardPermissionsPending}
              value={task?.Assignee?.Id}
              emptyText={t("Shared:Unassigned")}
              actionSuccess={onTaskModified}
              model={{ showClearButton: true }}
            />
          </div>
        </TaskDetail>
        <TaskCollaboratorsContainer
          task={task}
          boardPermissions={boardPermissions}
          label={t("Collaborators")}
        />
      </div>
    </>
  );
};
